import { formatGBP } from '@tradeoutlet/shared';
import { DateTime } from 'luxon';
import { formatClock, shiftTimeParts } from '@tradeoutlet/shared';
import { getClockPref } from './clockPref';
import { TZ } from './env';

export const dt = (iso: string, zone = TZ) => DateTime.fromISO(iso, { zone: 'utc' }).setZone(zone);

/** A time of day in the person's 12 / 24-hour clock (Settings → My account → Preferences): "14:30" or "2:30 PM". */
export const clock = (d: DateTime, compact = false) => formatClock(d, getClockPref(), compact);

export function timeOfDay(iso: string, zone = TZ) { return clock(dt(iso, zone)); }

export function messageTimestamp(iso: string) {
  const d = dt(iso);
  const now = DateTime.now().setZone(TZ);
  if (d.hasSame(now, 'day')) return clock(d);
  if (d.hasSame(now.minus({ days: 1 }), 'day')) return `Yesterday ${clock(d)}`;
  if (d.hasSame(now, 'year')) return `${d.toFormat('d LLL')}, ${clock(d)}`;
  return `${d.toFormat('d LLL yyyy')}, ${clock(d)}`;
}

/** t263u: "Today 14:32", "Yesterday 09:05", "16 Oct, 14:32" (older years carry the year), in Europe/London. */
export function whenStamp(iso: string) {
  const d = dt(iso);
  return d.hasSame(DateTime.now().setZone(TZ), 'day') ? `Today ${clock(d)}` : messageTimestamp(iso);
}

export function dayDivider(iso: string) {
  const d = dt(iso);
  const now = DateTime.now().setZone(TZ);
  if (d.hasSame(now, 'day')) return 'Today';
  if (d.hasSame(now.minus({ days: 1 }), 'day')) return 'Yesterday';
  return d.toFormat(d.hasSame(now, 'year') ? 'cccc d LLLL' : 'cccc d LLLL yyyy');
}

/**
 * How long ago something happened: "just now" (under a minute, and anything slightly in the future from clock skew or
 * an optimistic timestamp), "1 min ago", "5 mins ago", "3 hours ago", "yesterday", then an en-GB date ("14 Oct",
 * "14 Oct 2025"). Never "in …": use `relativeFuture` for things that really are ahead (due dates, retention).
 */
export function relative(iso: string, now: DateTime = DateTime.now().setZone(TZ)) {
  const d = dt(iso);
  if (!d.isValid) return '';
  const secs = Math.max(0, now.diff(d, 'seconds').seconds);
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return mins === 1 ? '1 min ago' : `${mins} mins ago`;
  const hours = Math.floor(mins / 60);
  // Hours for today, and for the small hours (10 pm last night reads "2 hours ago" at 00:30, not "yesterday").
  if (hours < 24 && (d.hasSame(now, 'day') || hours < 6)) return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
  if (d.hasSame(now.minus({ days: 1 }), 'day')) return 'yesterday';
  return d.toFormat(d.hasSame(now, 'year') ? 'd LLL' : 'd LLL yyyy');
}

/** For genuinely future moments only ("in 3 days", "in 2 hours"); past or now reads "now". */
export function relativeFuture(iso: string, now: DateTime = DateTime.now().setZone(TZ)) {
  const d = dt(iso);
  if (!d.isValid) return '';
  if (d <= now) return 'now';
  return d.toRelative({ base: now, locale: 'en-GB' }) ?? '';
}

/** Full date and time for a hover, in the user's 12/24-hour clock: "Sunday 18 October 2026, 18:55". */
export function fullDateTime(iso: string) {
  const d = dt(iso);
  return d.isValid ? `${d.toFormat('cccc d LLLL yyyy')}, ${clock(d)}` : '';
}

export function formatDate(date: string | null | undefined, fmt = 'd LLL') {
  if (!date) return '';
  return DateTime.fromISO(date, { zone: TZ }).toFormat(fmt);
}

export function minutes(min: number) {
  const sign = min < 0 ? '-' : '';
  const m = Math.abs(Math.round(min));
  const h = Math.floor(m / 60);
  const r = m % 60;
  return `${sign}${h}h${r ? ` ${String(r).padStart(2, '0')}m` : ''}`;
}

export function hours(h: number) { return `${Math.round(h * 100) / 100}h`; }

/** Money: the shared formatter (t171u), e.g. 150000 -> "£1,500.00". */
export function pounds(pence: number | null | undefined) { return formatGBP(pence); }

/** t310u: initials, the avatar palette and colorFor live in @tradeoutlet/shared (ui/design), shared with the native app. */
export { avatarInitials as initials } from '@tradeoutlet/shared';

export function today(zone = TZ) { return DateTime.now().setZone(zone).toISODate()!; }

export function fileSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export { AVATAR_PALETTE, colorFor } from '@tradeoutlet/shared';

/** "Thu 16 Oct, 09:00–17:30" in the building's zone (en-GB); overnight shifts end with the day: "Fri 30 Oct, 22:00–06:00 Sat". */
export function shiftWhen(s: { starts_at: string; ends_at: string }, zone = TZ) {
  const a = dt(s.starts_at, zone).setLocale('en-GB');
  const b = dt(s.ends_at, zone).setLocale('en-GB');
  return `${a.toFormat('ccc d LLL')}, ${shiftTimeParts(a, b, getClockPref()).text}`;
}

/**
 * UI review: the house date formats. Dates read "9 Jul 2026", times "14:05", and a date with a time puts a comma
 * between them ("Thu 9 Jul, 14:05"). `eslint.config` only allows these patterns in `toFormat`/`formatDate`.
 */
export const DATE_FORMATS = {
  time: 'HH:mm',
  day: 'd LLL',
  date: 'd LLL yyyy',
  weekday: 'ccc d LLL',
  weekdayDate: 'ccc d LLL yyyy',
  long: 'cccc d LLLL',
  longDate: 'cccc d LLLL yyyy',
  month: 'LLLL yyyy',
  /** Planner headers: "Oct 2026" and the weekday initial "M". */
  monthShort: 'LLL yyyy',
  dayInitial: 'ccccc',
  dayTime: 'd LLL, HH:mm',
  weekdayTime: 'ccc d LLL, HH:mm',
  dateTime: 'd LLL yyyy, HH:mm',
} as const;

const asDate = (v: string) => (/^\d{4}-\d{2}-\d{2}$/.test(v) ? DateTime.fromISO(v, { zone: TZ }) : dt(v));
/** Format an ISO date ("2026-07-09") or timestamp in the house style; `–` for empty values. */
export const fmt = {
  date: (v: string | null | undefined, empty = '–') => (v ? asDate(v).toFormat(DATE_FORMATS.date) : empty),
  day: (v: string | null | undefined, empty = '–') => (v ? asDate(v).toFormat(DATE_FORMATS.day) : empty),
  weekday: (v: string | null | undefined, empty = '–') => (v ? asDate(v).toFormat(DATE_FORMATS.weekday) : empty),
  time: (v: string | null | undefined, empty = '–') => (v ? clock(dt(v)) : empty),
  dateTime: (v: string | null | undefined, empty = '–') => (v ? `${dt(v).toFormat(DATE_FORMATS.date)}, ${clock(dt(v))}` : empty),
  weekdayTime: (v: string | null | undefined, empty = '–') => (v ? `${dt(v).toFormat(DATE_FORMATS.weekday)}, ${clock(dt(v))}` : empty),
  /** "9–12 Jul 2026", "30 Jun – 2 Jul 2026", "30 Dec 2026 – 2 Jan 2027". */
  range: (a: string, b: string) => {
    const x = asDate(a), y = asDate(b);
    if (x.hasSame(y, 'day')) return x.toFormat(DATE_FORMATS.date);
    if (x.hasSame(y, 'month')) return `${x.toFormat('d')}–${y.toFormat(DATE_FORMATS.date)}`;
    if (x.hasSame(y, 'year')) return `${x.toFormat(DATE_FORMATS.day)} – ${y.toFormat(DATE_FORMATS.date)}`;
    return `${x.toFormat(DATE_FORMATS.date)} – ${y.toFormat(DATE_FORMATS.date)}`;
  },
};

/** "in_progress" → "In progress": status labels in sentence case (UI review). */
export function sentence(s: string | null | undefined): string {
  if (!s) return '';
  const t = s.replace(/_/g, ' ');
  return t.charAt(0).toUpperCase() + t.slice(1);
}

/** Enum value to a sentence-case label: "pending_manager" → "Pending manager", "FULL_TIME" → "Full time". */
export function sentenceCase(v: string | null | undefined): string {
  const s = (v ?? '').replace(/[_-]+/g, ' ').trim().toLowerCase();
  return s ? s[0].toUpperCase() + s.slice(1) : '';
}

/** en-GB short date, with the year only when it isn't this year: "14 Nov", "14 Nov 2027". */
export function ukShortDate(iso: string, now: DateTime = DateTime.now().setZone(TZ)) {
  const d = dt(iso);
  return d.isValid ? d.toFormat(d.hasSame(now, 'year') ? 'd LLL' : 'd LLL yyyy') : '';
}

/**
 * Wording for a moment that is either still ahead or already past, so it can't be misread:
 * whenWording(iso, 'Transcript will be deleted on', 'Transcript deleted on') → "Transcript will be deleted on 14 Nov".
 */
export function whenWording(iso: string, future: string, past: string, opts: { time?: boolean; now?: DateTime } = {}) {
  const now = opts.now ?? DateTime.now().setZone(TZ);
  const d = dt(iso);
  const when = `${ukShortDate(iso, now)}${opts.time ? `, ${clock(d)}` : ''}`;
  return `${d > now ? future : past} ${when}`;
}

/**
 * Compact "last active" for the person sidebar (t173u): "just now", "5m ago", "2h ago", "yesterday", "3d ago", then an
 * en-GB date with the year ("19 Oct 2026") from a week back. Times slightly in the future (clock skew) read "just now".
 */
export function lastActiveAgo(iso: string, now: DateTime = DateTime.now().setZone(TZ)) {
  const d = dt(iso);
  if (!d.isValid) return '';
  const secs = Math.max(0, now.diff(d, 'seconds').seconds);
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24 && (d.hasSame(now, 'day') || hours < 6)) return `${hours}h ago`;
  if (d.hasSame(now.minus({ days: 1 }), 'day')) return 'yesterday';
  const days = Math.floor(now.startOf('day').diff(d.startOf('day'), 'days').days);
  if (days < 7) return `${days}d ago`;
  return d.toFormat('d LLL yyyy');
}

/** Exact date and time for a hover, in Europe/London and the person's clock: "20 Oct 2026, 11:20". */
export function exactDateTime(iso: string) {
  const d = dt(iso);
  return d.isValid ? `${d.toFormat('d LLL yyyy')}, ${clock(d)}` : '';
}

/**
 * t267u: chat message and thread times. Under a minute "Just now", then "1 min ago" … "59 min ago"; from an hour the
 * usual stamp: the time for today ("14:05"), "Yesterday 14:05", "18 Oct, 06:05", then "18 Oct 2025, 06:05". Days are
 * Europe/London days. A time slightly ahead (clock skew, an optimistic send) reads "Just now". `inline` lower-cases the
 * words for use mid-sentence ("Last reply just now", "Last reply yesterday 23:52"). Pair it with the shared ticker
 * (`MessageTime` in components/time/RelativeTime) so it stays live, and `fullDateTime` for the hover.
 */
export function messageTime(iso: string, opts: { now?: DateTime; inline?: boolean } = {}) {
  const d = dt(iso);
  if (!d.isValid) return '';
  const now = (opts.now ?? DateTime.now()).setZone(TZ);
  const secs = Math.max(0, now.diff(d, 'seconds').seconds);
  if (secs < 60) return opts.inline ? 'just now' : 'Just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins} min ago`;
  if (d.hasSame(now, 'day')) return clock(d);
  if (d.hasSame(now.minus({ days: 1 }), 'day')) return `${opts.inline ? 'yesterday' : 'Yesterday'} ${clock(d)}`;
  if (d.hasSame(now, 'year')) return `${d.toFormat('d LLL')}, ${clock(d)}`;
  return `${d.toFormat('d LLL yyyy')}, ${clock(d)}`;
}
